<!--
  Form View Component Template
  ===========================
  
  Admin-only template for viewing and managing submitted form entries.
  This template provides a comprehensive interface for admins to:
  - View complete form submissions with all data and attachments
  - Generate and download PDFs for printing or archiving
  - Resend emails to standard or custom recipients
  - Navigate between related form entries
  - Create new forms of the same type
  
  Responsive Design:
  - Mobile: Action sheets and simplified navigation
  - Desktop: Popover dropdowns and full button layouts
  
  Key Features:
  - Dynamic form rendering via app-form-render component
  - Attachment viewing modal for images, PDFs, and documents
  - PDF generation with loading states and caching
  - Email functionality with recipient management
  - Navigation options for form entry exploration
  
  @author MacSafety Development Team
  @version 1.0
  @access Admin Only
-->

<ion-content class=" ion-safe-area-bottom ion-safe-area-top" >
  <!-- Form Entry Header Section -->
  <div class="form-entry-view-header">
  <!-- Template-only title -->
  <ion-text class="template-title">
    <h2 class="ion-no-padding ion-no-margin ion-margin-bottom">{{ formTitle }}</h2>
  </ion-text>
  
  <!-- Mobile Navigation and Actions -->
  <!-- Back/Navigation Actions -->
  <ion-item lines="none" *ngIf="isMobile()">
    <ion-button 
      slot="start" 
      fill="outline" 
      color="medium" 
      (click)="presentNavigationSheet()">
      <ion-icon name="arrow-back-outline" slot="start"></ion-icon>
      Back/View
    </ion-button>
    
    <!-- Form Actions -->
    <ion-button 
      slot="end" 
      fill="solid" 
      color="primary" 
      (click)="presentActionSheet()"
      class="ion-padding-start">
      <ion-icon name="ellipsis-horizontal" slot="start"></ion-icon>
      Actions
    </ion-button>
  </ion-item>

  <!-- Desktop Navigation and Actions Grid -->
   <ion-grid *ngIf="!isMobile()" class="ion-no-margin ion-no-padding">
    <ion-row class="ion-no-margin ion-no-padding">
     
      <ion-col class="ion-no-margin">
        <ion-item lines="none" class="ion-no-margin ion-no-padding">
          <ion-buttons slot="start">
      
            <!-- GO BACK Dropdown Trigger -->
            <ion-button 
              id="go-back-trigger" 
              class="no-print ion-text-center" 
              fill="clear" 
              color="tertiary">
              <ion-icon name="arrow-back-outline" slot="start"></ion-icon>
              Go Back
              <ion-icon name="chevron-down" slot="end" size="small"></ion-icon>
            </ion-button>
      
            <!-- GO BACK Popover -->
            <ion-popover 
              trigger="go-back-trigger"
              trigger-action="hover"
              [dismissOnSelect]="true"
              side="bottom"
              alignment="start">
              <ng-template>
                <ion-content>
                  <ion-list lines="none">
                    <ion-item 
                      button 
                      (click)="viewEntriesByType(formType)">
                      <ion-icon name="albums-outline" slot="start" color="tertiary"></ion-icon>
                      <ion-label>View All by Type</ion-label>
                    </ion-item>
      
                    <ion-item 
                      button 
                      (click)="viewEntriesBySubmitter(formEntry.submittedByName)">
                      <ion-icon name="person-outline" slot="start" color="tertiary"></ion-icon>
                      <ion-label>View All by Submitter</ion-label>
                    </ion-item>
                  </ion-list>
                </ion-content>
              </ng-template>
            </ion-popover>
      
          </ion-buttons>
        </ion-item>
      </ion-col>
      
    <ion-col size="auto" class="action-buttons-col ion-no-margin">
      <ion-item lines="none" class="action-buttons-item">
    <ion-buttons slot="end" class="equal-width-buttons action-buttons">

            <!-- PDF Actions Dropdown -->
            <ion-button 
            id="pdf-trigger" 
            class="no-print ion-text-center" 
            fill="outline" 
            color="dark" 
            [disabled]="pdfLoading().isLoading">
            <ion-spinner *ngIf="pdfLoading().isLoading" name="circular" size="small" slot="start"></ion-spinner>
            <ion-icon *ngIf="!pdfLoading().isLoading" name="document-attach-outline" slot="start" ></ion-icon>
            PDF
            <ion-icon name="chevron-down"  size="small" slot="end"></ion-icon>
          </ion-button>
    
          <ion-popover 
            trigger="pdf-trigger" 
            trigger-action="click"
            [dismissOnSelect]="true"
            side="bottom"
            alignment="start">
            <ng-template>
              <ion-content>
                <ion-list lines="none">
                  <ion-item 
                    button 
                    (click)="printEntry()" 
                    [disabled]="pdfLoading().isLoading">
                    <ion-icon name="print-outline" slot="start" color="primary"></ion-icon>
                    <ion-label>Print</ion-label>
                  </ion-item>
                  
                  <ion-item 
                    button 
                    (click)="downloadEntry()" 
                    [disabled]="pdfLoading().isLoading">
                    <ion-icon name="download-outline" slot="start" color="primary"></ion-icon>
                    <ion-label>Download</ion-label>
                  </ion-item>
                </ion-list>
              </ion-content>
            </ng-template>
          </ion-popover>


      <!-- Email Actions Dropdown -->
      <ion-button 
        id="email-trigger" 
        class="no-print ion-text-center" 
        fill="outline" 
        color="secondary" 
        [disabled]="emailLoading().isLoading"
        >
        <ion-spinner *ngIf="emailLoading().isLoading" name="circular" slot="start" size="small"></ion-spinner>
        <ion-icon *ngIf="!emailLoading().isLoading" name="send-outline" slot="start"></ion-icon>
        Email
        <ion-icon name="chevron-down" slot="end" size="small"></ion-icon>
      </ion-button>

      <ion-popover
        #emailPopover
        trigger="email-trigger"
        trigger-action="click"
        [dismissOnSelect]="false"
        (didDismiss)="selectedEmailMode = null"
        side="bottom"
        alignment="start">
        <ng-template>
          <ion-content>
            <!-- Step 1: Delivery Mode Selection -->
            <ion-list lines="none" *ngIf="selectedEmailMode === null">
              <ion-item
                button
                (click)="selectedEmailMode = 'pdf'"
                [disabled]="emailLoading().isLoading">
                <ion-icon name="document-attach-outline" slot="start" color="primary"></ion-icon>
                <ion-label>
                  <h3>Email with PDF</h3>
                  <p>Send the form as a PDF attachment</p>
                </ion-label>
              </ion-item>

              <ion-item
                button
                (click)="selectedEmailMode = 'html'"
                [disabled]="emailLoading().isLoading">
                <ion-icon name="mail-outline" slot="start" color="primary"></ion-icon>
                <ion-label>
                  <h3>Email in Body</h3>
                  <p>Send the form in the email body</p>
                </ion-label>
              </ion-item>
            </ion-list>

            <!-- Step 2: Recipient Selection for the chosen delivery mode -->
            <ion-list lines="none" *ngIf="selectedEmailMode !== null">
              <ion-item
                button
                (click)="selectedEmailMode = null">
                <ion-icon name="arrow-back-outline" slot="start" color="medium"></ion-icon>
                <ion-label>
                  <p>{{ selectedEmailMode === 'pdf' ? 'Email with PDF' : 'Email in Body' }}</p>
                </ion-label>
              </ion-item>

              <ion-item
                button
                (click)="resendEmailStandard(selectedEmailMode || 'pdf'); emailPopover.dismiss()"
                [disabled]="emailLoading().isLoading">
                <ion-icon name="refresh-outline" slot="start" color="primary"></ion-icon>
                <ion-label>
                  <h3>Standard Recipients</h3>
                  <p>Send to default configured recipients</p>
                </ion-label>
              </ion-item>

              <ion-item
                button
                (click)="presentCustomRecipientsAlert(selectedEmailMode || 'pdf'); emailPopover.dismiss()"
                [disabled]="emailLoading().isLoading">
                <ion-icon name="people-outline" slot="start" color="primary"></ion-icon>
                <ion-label>
                  <h3>Custom Recipients</h3>
                  <p>Choose specific email addresses</p>
                </ion-label>
              </ion-item>
            </ion-list>

            <!-- Cache Status Indicator (PDF mode only) -->
            <ion-item lines="none" class="cache-status-item" *ngIf="selectedEmailMode === 'pdf'">
              <ion-icon
                [name]="isPdfCached ? 'checkmark-circle' : 'hourglass-outline'"
                [color]="isPdfCached ? 'success' : 'warning'"
                slot="start">
              </ion-icon>
              <ion-label class="cache-status-label">
                <p>{{ isPdfCached ? 'PDF cached (fast send)' : 'PDF will be generated' }}</p>
              </ion-label>
            </ion-item>
          </ion-content>
        </ng-template>
      </ion-popover>
      

      <ion-button class="no-print" fill="solid" color="primary" (click)="createNew(formType)">
        <ion-icon name="add-circle-outline" slot="start"></ion-icon>
        Create New
      </ion-button>
    </ion-buttons>  
    </ion-item>
    </ion-col>

    </ion-row>
  </ion-grid>
</div>
    

  <!-- Main Content Area - Form Display and Rendering -->
  <div class="scrollable-list-container">
      <!-- Loading and Empty States -->
  <div *ngIf="dataLoading().isLoading" class="ion-text-center ion-padding loading-state">
    <ion-spinner name="circular"></ion-spinner>
    <p>{{ dataLoading().message || 'Loading form entry...' }}</p>
  </div>

  <div *ngIf="!dataLoading().isLoading && !formEntry" class="ion-text-center ion-padding empty-state">
    <ion-icon name="document-outline" size="large" color="medium"></ion-icon>
    <p>No form entry found.</p>
  </div>
    <app-form-render 
      *ngIf="formEntry"
      [formEntry]="formEntry"
      [formSteps]="formSteps"
      [fieldLabels]="fieldLabels"
      [formTitle]="formTitle"
      [attachments]="attachments"
      [fieldMetadata]="fieldMetadata"
      [wordPressFields]="wordPressFields"
      (attachmentClick)="openAttachmentModal($event)">
    </app-form-render>
  </div>


<!-- Attachment Viewer Modal - Fullscreen Modal for Viewing Attachments -->
<ion-modal 
  [isOpen]="isAttachmentModalOpen" 
  (didDismiss)="closeAttachmentModal()" 
  [backdropDismiss]="true" 
  [cssClass]="'fullscreen-modal ignore-dark-mode'" 
  [backdropOpacity]="0.9"
>
<ng-template>
      <!-- Floating Close Button -->
      <ion-fab vertical="top" horizontal="end" slot="fixed">
        <ion-fab-button 
          size="small" 
          color="light" 
          (click)="closeAttachmentModal()"
          class="modal-close-fab"
        >
          <ion-icon name="close"></ion-icon>
        </ion-fab-button>
      </ion-fab>
  
  <app-attachment-viewer-modal

    [isOpen]="isAttachmentModalOpen"
    [attachments]="modalAttachments"
    [initialIndex]="selectedAttachmentIndex"
    (closeModal)="closeAttachmentModal()"
  ></app-attachment-viewer-modal>
</ng-template>
</ion-modal>


<!-- Loading Overlays for PDF and Email Operations -->
<!-- PDF Loading Overlay -->
<div *ngIf="pdfLoading().isLoading" class="loading-overlay ">

  <ion-spinner name="circular" size="large"></ion-spinner>
  <p>{{ pdfLoading().message || 'Generating PDF...' }}</p>

</div>

<!-- Email Loading Overlay -->
<div *ngIf="emailLoading().isLoading" class="loading-overlay">

  <ion-spinner name="circular" size="large" color="secondary"></ion-spinner>
  <p>{{ emailLoading().message || 'Sending email...' }}</p>

</div>
</ion-content>




